import { useReducer, useCallback, useRef, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { chatsApi, streamMessage } from '@/api/chats'
import { getErrorMessage, getErrorStatus } from '@/api/errors'
import { readContainerPreference, readEffortPreference } from '@/lib/chatPreferences'
import { useTenantKey } from './useTenantKey'

interface PerChatState {
  isStreaming: boolean
  streamingLogs: string
  containerStatus: string | null
  usesContainer: boolean
  pendingUserMessage: string | null
  activeSessionId: string | null
  isCancelling: boolean
  startedAt: string | null
  effort: string | null
}

const MAX_RECONNECT_SESSION_MS = 1_860_000
const DEFAULT_SEND_FAILURE_MESSAGE = 'Your message could not be sent. Please try again.'

export interface ChatSendError {
  message: string
  unsentMessage: string
}

function withoutChat(errors: Record<string, ChatSendError>, chatId: string): Record<string, ChatSendError> {
  const next = { ...errors }
  delete next[chatId]
  return next
}

const defaultChatState: PerChatState = {
  isStreaming: false,
  streamingLogs: '',
  containerStatus: null,
  usesContainer: false,
  pendingUserMessage: null,
  activeSessionId: null,
  isCancelling: false,
  startedAt: null,
  effort: null,
}

type StreamState = Record<string, PerChatState>

type StreamAction =
  | { type: 'START_STREAM'; chatId: string; message: string; useContainer: boolean; effort: string | null }
  | { type: 'START_RECONNECT'; chatId: string; sessionId: string; startedAt: string; useContainer: boolean; effort: string | null }
  | { type: 'SESSION_CREATED'; chatId: string; sessionId: string }
  | { type: 'LOG_DELTA'; chatId: string; content: string }
  | { type: 'STATUS'; chatId: string; status: string }
  | { type: 'STREAM_END'; chatId: string }
  | { type: 'CANCEL_REQUESTED'; chatId: string }
  | { type: 'CANCEL_FAILED'; chatId: string }

function streamReducer(state: StreamState, action: StreamAction): StreamState {
  const current = state[action.chatId] || defaultChatState

  switch (action.type) {
    case 'START_STREAM':
      return {
        ...state,
        [action.chatId]: {
          isStreaming: true,
          streamingLogs: '',
          containerStatus: null,
          usesContainer: action.useContainer,
          pendingUserMessage: action.message,
          activeSessionId: null,
          isCancelling: false,
          startedAt: new Date().toISOString(),
          effort: action.effort,
        },
      }
    case 'START_RECONNECT':
      return {
        ...state,
        [action.chatId]: {
          ...defaultChatState,
          isStreaming: true,
          containerStatus: 'reconnected',
          usesContainer: action.useContainer,
          activeSessionId: action.sessionId,
          startedAt: action.startedAt,
          effort: action.effort,
        },
      }
    case 'SESSION_CREATED':
      return {
        ...state,
        [action.chatId]: { ...current, activeSessionId: action.sessionId },
      }
    case 'LOG_DELTA':
      return {
        ...state,
        [action.chatId]: {
          ...current,
          streamingLogs: current.streamingLogs + action.content,
        },
      }
    case 'STATUS':
      return {
        ...state,
        [action.chatId]: { ...current, containerStatus: action.status },
      }
    case 'STREAM_END': {
      const { [action.chatId]: _, ...rest } = state
      return rest
    }
    case 'CANCEL_REQUESTED':
      return {
        ...state,
        [action.chatId]: { ...current, isCancelling: true },
      }
    case 'CANCEL_FAILED':
      return {
        ...state,
        [action.chatId]: { ...current, isCancelling: false },
      }
    default:
      return state
  }
}

export function useChatStream(projectId: string, activeChatId: string | null) {
  const [state, dispatch] = useReducer(streamReducer, {})
  const [sendErrors, setSendErrors] = useState<Record<string, ChatSendError>>({})
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())
  const reconnectAbortRef = useRef<AbortController | null>(null)
  const activeStreamTokenRef = useRef<Map<string, symbol>>(new Map())
  const queuedCancelTokenRef = useRef<Map<string, symbol>>(new Map())
  const completedSessionIdsRef = useRef<Set<string>>(new Set())
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  const cancelSessionForChat = useCallback(async (chatId: string, sessionId: string) => {
    try {
      await chatsApi.cancelSession(projectId, chatId, sessionId)
    } catch (err) {
      if (getErrorStatus(err) !== 404) {
        dispatch({ type: 'CANCEL_FAILED', chatId })
        toast.error(getErrorMessage(err) ?? 'Failed to stop the response. Please try again')
        return
      }
    }

    const controller = abortControllersRef.current.get(chatId)
    controller?.abort()
    if (controller && abortControllersRef.current.get(chatId) === controller) {
      abortControllersRef.current.delete(chatId)
    }
    dispatch({ type: 'STREAM_END', chatId })
    queryClient.invalidateQueries({
      queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
    })
  }, [projectId, queryClient, tenantKey])

  const currentState = activeChatId ? (state[activeChatId] || defaultChatState) : defaultChatState
  const sendError = activeChatId ? (sendErrors[activeChatId] ?? null) : null

  const dismissSendError = useCallback(() => {
    if (!activeChatId) return
    setSendErrors((prev) => withoutChat(prev, activeChatId))
  }, [activeChatId])

  const clearUnsentMessage = useCallback((chatId: string) => {
    setSendErrors((prev) => {
      const current = prev[chatId]
      return current ? { ...prev, [chatId]: { ...current, unsentMessage: '' } } : prev
    })
  }, [])

  // Abort all active streams and reconnect polls on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach((controller) => controller.abort())
      reconnectAbortRef.current?.abort()
    }
  }, [])

  // When the active chat changes, check if there is an in-progress session to reconnect to.
  // This handles the case where the user navigated away while a session was running.
  useEffect(() => {
    if (!activeChatId || !projectId) return

    const chatId = activeChatId

    // If we are already streaming for this chat (user just sent a message), skip the check.
    if (abortControllersRef.current.has(chatId)) return

    reconnectAbortRef.current?.abort()
    reconnectAbortRef.current = new AbortController()
    const signal = reconnectAbortRef.current.signal

    const fetchSession = async () => {
      try {
        return await chatsApi.getLatestSession(projectId, chatId)
      } catch {
        return undefined
      }
    }

    const checkAndReconnect = async () => {
      let initial = await fetchSession()
      while (initial === undefined && !signal.aborted) {
        await new Promise<void>((resolve) => setTimeout(resolve, 2000))
        initial = await fetchSession()
      }

      if (signal.aborted || !initial) return
      if (initial.status === 'Completed' || initial.status === 'Failed' || initial.status === 'Cancelled') return
      if (completedSessionIdsRef.current.has(initial.id)) return

      const startedAtMs = initial.startedAt ? new Date(initial.startedAt).getTime() : Date.now()
      const deadlineMs = startedAtMs + MAX_RECONNECT_SESSION_MS

      if (Date.now() >= deadlineMs) return

      dispatch({
        type: 'START_RECONNECT',
        chatId,
        sessionId: initial.id,
        startedAt: new Date(startedAtMs).toISOString(),
        useContainer: readContainerPreference(chatId, true),
        effort: readEffortPreference(chatId, null),
      })

      let reachedTerminal = false
      while (!signal.aborted && Date.now() < deadlineMs) {
        await new Promise<void>((resolve) => setTimeout(resolve, 2000))
        if (signal.aborted) break

        const updated = await fetchSession()
        if (updated === undefined) continue
        if (!updated || updated.status === 'Completed' || updated.status === 'Failed' || updated.status === 'Cancelled') {
          reachedTerminal = true
          break
        }
      }

      dispatch({ type: 'STREAM_END', chatId })
      if (reachedTerminal || !signal.aborted) {
        queryClient.invalidateQueries({
          queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
        })
        queryClient.invalidateQueries({
          queryKey: tenantKey('projects', projectId, 'chats'),
        })
      }
    }

    checkAndReconnect()

    return () => {
      reconnectAbortRef.current?.abort()
    }
  }, [activeChatId, projectId, queryClient])

  const sendMessage = useCallback(
    async (message: string, attachmentIds?: string[], useContainer = true, model?: string, effort?: string, skillIds?: string[]) => {
      if (!activeChatId) return { sent: false, messageCreated: false }

      if (abortControllersRef.current.has(activeChatId)) {
        toast.error('A response is still finishing up for this chat. Please wait a moment and try again.')
        return { sent: false, messageCreated: false }
      }

      reconnectAbortRef.current?.abort()
      dispatch({ type: 'STREAM_END', chatId: activeChatId })

      const chatId = activeChatId
      setSendErrors((prev) => withoutChat(prev, chatId))

      dispatch({ type: 'START_STREAM', chatId, message, useContainer, effort: effort ?? null })

      const streamToken = Symbol('chatStream')
      activeStreamTokenRef.current.set(chatId, streamToken)
      queuedCancelTokenRef.current.delete(chatId)

      const abortController = new AbortController()
      abortControllersRef.current.set(chatId, abortController)
      const signal = abortController.signal

      const currentSessionRef = { id: null as string | null }
      let failureReported = false
      let handledWithoutSession = false

      const reportFailure = (text: string) => {
        failureReported = true
        if (currentSessionRef.id) {
          toast.error(text)
          return
        }
        setSendErrors((prev) => ({ ...prev, [chatId]: { message: text, unsentMessage: message } }))
      }

      // Map session API status to container status used by the UI
      const mapSessionStatus = (status: string): string | null => {
        switch (status) {
          case 'Pending':
          case 'Creating':
            return 'creating_container'
          case 'Running':
            return 'running'
          default:
            return null
        }
      }

      const sleep = (ms: number) =>
        new Promise<void>((resolve) => {
          if (signal.aborted) {
            resolve()
            return
          }
          const timer = setTimeout(resolve, ms)
          signal.addEventListener('abort', () => {
            clearTimeout(timer)
            resolve()
          }, { once: true })
        })

      const pollSession = async () => {
        await sleep(2000)

        while (!signal.aborted) {
          try {
            const session = await chatsApi.getLatestSession(projectId, chatId)
            if (signal.aborted) break

            if (session) {
              const isCurrentSession = currentSessionRef.id != null && session.id === currentSessionRef.id

              const mapped = mapSessionStatus(session.status)
              if (mapped) {
                dispatch({ type: 'STATUS', chatId, status: mapped })
              }

              if (isCurrentSession && (session.status === 'Completed' || session.status === 'Failed' || session.status === 'Cancelled')) {
                if (session.status === 'Failed') {
                  reportFailure(session.errorMessage ?? 'An unexpected error occurred while processing your message. Please try again.')
                }
                abortController.abort()
                break
              }
            }
          } catch {}

          await sleep(3000)
        }
      }

      // Run SSE stream and polling in parallel
      const pollPromise = pollSession()

      try {
        await streamMessage(
          projectId,
          chatId,
          message,
          (event, data) => {
            try {
              const parsed = JSON.parse(data)

              switch (event) {
                case 'session_created':
                  currentSessionRef.id = parsed.sessionId
                  dispatch({ type: 'SESSION_CREATED', chatId, sessionId: parsed.sessionId })
                  if (queuedCancelTokenRef.current.get(chatId) === streamToken) {
                    queuedCancelTokenRef.current.delete(chatId)
                    void cancelSessionForChat(chatId, parsed.sessionId)
                  }
                  break

                case 'log_delta':
                  dispatch({ type: 'LOG_DELTA', chatId, content: parsed.content || '' })
                  break

                case 'status':
                  dispatch({ type: 'STATUS', chatId, status: parsed.status })
                  break

                case 'message_complete':
                  if (currentSessionRef.id) completedSessionIdsRef.current.add(currentSessionRef.id)
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  break

                case 'title_updated':
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats'),
                  })
                  break

                case 'connector_expired':
                case 'connector_missing':
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  break

                case 'context_budget_exhausted':
                  handledWithoutSession = true
                  queryClient.invalidateQueries({
                    queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
                  })
                  dispatch({ type: 'STREAM_END', chatId })
                  break

                case 'skills_unresolved':
                  if (parsed.message) toast.error(parsed.message)
                  break

                case 'error':
                  console.error('Stream error:', parsed)
                  reportFailure(parsed.message || DEFAULT_SEND_FAILURE_MESSAGE)
                  dispatch({ type: 'STREAM_END', chatId })
                  break
              }
            } catch {
              console.error('Failed to parse SSE data:', data)
            }
          },
          signal,
          attachmentIds,
          useContainer,
          model,
          effort,
          skillIds,
        )
        if (!failureReported && !handledWithoutSession && !signal.aborted && currentSessionRef.id == null) {
          reportFailure(DEFAULT_SEND_FAILURE_MESSAGE)
        }
        return { sent: !(failureReported && currentSessionRef.id == null), messageCreated: currentSessionRef.id != null }
      } catch (err) {
        if (!signal.aborted && !(err instanceof Error && err.name === 'AbortError')) {
          console.error('Stream failed:', err)
          reportFailure(getErrorMessage(err) || (err instanceof Error ? err.message : '') || DEFAULT_SEND_FAILURE_MESSAGE)
        }
        return { sent: false, messageCreated: currentSessionRef.id != null }
      } finally {
        abortController.abort()
        await pollPromise
        dispatch({ type: 'STREAM_END', chatId })
        if (activeStreamTokenRef.current.get(chatId) === streamToken) {
          activeStreamTokenRef.current.delete(chatId)
        }
        queuedCancelTokenRef.current.delete(chatId)
        if (abortControllersRef.current.get(chatId) === abortController) {
          abortControllersRef.current.delete(chatId)
        }
        queryClient.invalidateQueries({
          queryKey: tenantKey('projects', projectId, 'chats', chatId, 'messages'),
        })
        queryClient.invalidateQueries({
          queryKey: tenantKey('projects', projectId, 'chats'),
        })
      }
    },
    [projectId, activeChatId, queryClient, tenantKey, cancelSessionForChat],
  )

  const cancelStream = useCallback(async () => {
    if (!activeChatId) return

    if (!currentState.isStreaming) return

    dispatch({ type: 'CANCEL_REQUESTED', chatId: activeChatId })

    const sessionId = currentState.activeSessionId
    if (sessionId) {
      await cancelSessionForChat(activeChatId, sessionId)
    } else {
      const streamToken = activeStreamTokenRef.current.get(activeChatId)
      if (streamToken) queuedCancelTokenRef.current.set(activeChatId, streamToken)
    }
  }, [activeChatId, currentState.isStreaming, currentState.activeSessionId, cancelSessionForChat])

  return {
    sendMessage,
    cancelStream,
    isStreaming: currentState.isStreaming,
    isCancelling: currentState.isCancelling,
    streamingLogs: currentState.streamingLogs,
    containerStatus: currentState.containerStatus,
    usesContainer: currentState.usesContainer,
    pendingUserMessage: currentState.pendingUserMessage,
    activeSessionId: currentState.activeSessionId,
    sendError,
    dismissSendError,
    clearUnsentMessage,
    startedAt: currentState.startedAt,
    effort: currentState.effort,
  }
}
